import { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Pencil } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import ImportDialog from "../components/ImportDialog";

const EMPTY = {
  name: "", contact: "", phone: "", email: "", address: "",
  npwp: "", nib: "", sppkp: "",
  bank_name: "", bank_account: "", bank_account_name: "",
};

export default function Suppliers() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const load = () => api.get("/suppliers").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditingId(null); setOpen(true); };
  const openEdit = (s) => { setForm({ ...EMPTY, ...s }); setEditingId(s.id); setOpen(true); };

  const save = async () => {
    if (!form.name) return toast.error("Nama supplier wajib");
    try {
      if (editingId) await api.put(`/suppliers/${editingId}`, form);
      else await api.post("/suppliers", form);
      toast.success(editingId ? "Supplier diperbarui" : "Supplier ditambahkan");
      setOpen(false); setEditingId(null); setForm(EMPTY); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus supplier ini?")) return;
    await api.delete(`/suppliers/${id}`); load();
  };

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] font-medium uppercase tracking-widest text-slate-500">Direktori</div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900 tracking-tight">Supplier</h1>
          <p className="text-sm text-slate-500 mt-1">Data legalitas (NPWP/NIB/SPPKP) & rekening bank opsional — dapat diisi bila tersedia.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="supplier-import-btn"><Upload size={14} className="mr-1" />Import Excel</Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} className="bg-navy-900 hover:bg-navy-800 text-white" data-testid="supplier-add-btn"><Plus size={16} className="mr-2" />Tambah Supplier</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display">{editingId ? "Edit Supplier" : "Supplier Baru"}</DialogTitle></DialogHeader>
              <div className="space-y-4 text-sm">
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-2">Data Umum</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2"><Label>Nama Supplier *</Label><Input data-testid="supplier-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                    <div><Label>Kontak / PIC</Label><Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} /></div>
                    <div><Label>Telepon</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                    <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                    <div className="col-span-2"><Label>Alamat</Label><Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                  </div>
                </div>

                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-2">Legalitas <span className="normal-case font-normal text-slate-400">(opsional)</span></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>NPWP</Label><Input placeholder="00.000.000.0-000.000" value={form.npwp} onChange={(e) => setForm({ ...form, npwp: e.target.value })} data-testid="supplier-npwp-input" /></div>
                    <div><Label>NIB</Label><Input placeholder="1234567890123" value={form.nib} onChange={(e) => setForm({ ...form, nib: e.target.value })} data-testid="supplier-nib-input" /></div>
                    <div className="col-span-2"><Label>SPPKP</Label><Input placeholder="Nomor Surat Pengukuhan PKP" value={form.sppkp} onChange={(e) => setForm({ ...form, sppkp: e.target.value })} data-testid="supplier-sppkp-input" /></div>
                  </div>
                </div>

                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-2">Rekening Bank <span className="normal-case font-normal text-slate-400">(opsional)</span></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Nama Bank</Label><Input placeholder="Cth: BCA / Mandiri" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} data-testid="supplier-bank-name-input" /></div>
                    <div><Label>Nomor Rekening</Label><Input placeholder="1234567890" value={form.bank_account} onChange={(e) => setForm({ ...form, bank_account: e.target.value })} data-testid="supplier-bank-acc-input" /></div>
                    <div className="col-span-2"><Label>Atas Nama</Label><Input placeholder="Nama pemilik rekening" value={form.bank_account_name} onChange={(e) => setForm({ ...form, bank_account_name: e.target.value })} data-testid="supplier-bank-holder-input" /></div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
                <Button className="bg-navy-900 hover:bg-navy-800 text-white" onClick={save} data-testid="supplier-save-btn">Simpan</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="card-elevated"><CardContent className="p-4">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nama</TableHead><TableHead>Kontak</TableHead><TableHead>Telepon</TableHead>
                <TableHead>NPWP</TableHead><TableHead>NIB</TableHead><TableHead>Rekening</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((s) => (
                <TableRow key={s.id}>
                  <TableCell>
                    <div className="font-medium">{s.name}</div>
                    {s.address && <div className="text-xs text-slate-500">{s.address}</div>}
                  </TableCell>
                  <TableCell>{s.contact || <span className="text-slate-400">—</span>}</TableCell>
                  <TableCell className="font-mono text-xs">{s.phone || <span className="text-slate-400">—</span>}</TableCell>
                  <TableCell className="font-mono text-xs">{s.npwp || <span className="text-slate-400">—</span>}</TableCell>
                  <TableCell className="font-mono text-xs">{s.nib || <span className="text-slate-400">—</span>}</TableCell>
                  <TableCell className="text-xs">
                    {s.bank_account ? (
                      <div>
                        <div className="font-mono">{s.bank_name} {s.bank_account}</div>
                        {s.bank_account_name && <div className="text-slate-500">a.n. {s.bank_account_name}</div>}
                      </div>
                    ) : <span className="text-slate-400">—</span>}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {(user.role === "admin" || user.role === "staff_purchasing") && (
                      <Button size="sm" variant="ghost" onClick={() => openEdit(s)} data-testid={`supplier-edit-${s.id}`}><Pencil size={14} /></Button>
                    )}
                    {user.role === "admin" && <Button size="sm" variant="ghost" onClick={() => remove(s.id)}><Trash2 size={14} className="text-red-500" /></Button>}
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">Belum ada supplier</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </CardContent></Card>

      <ImportDialog
        open={importOpen} onClose={() => setImportOpen(false)} onSuccess={load}
        title="Import Supplier dari Excel/CSV"
        endpoint="/suppliers/import"
        templateName="template_supplier"
        columns={[
          { key: "name", label: "Nama Supplier", required: true, type: "string" },
          { key: "contact", label: "Kontak", type: "string" },
          { key: "phone", label: "Telepon", type: "string" },
          { key: "email", label: "Email", type: "string" },
          { key: "address", label: "Alamat", type: "string" },
          { key: "npwp", label: "NPWP", type: "string" },
          { key: "nib", label: "NIB", type: "string" },
          { key: "sppkp", label: "SPPKP", type: "string" },
          { key: "bank_name", label: "Nama Bank", type: "string" },
          { key: "bank_account", label: "No. Rekening", type: "string" },
          { key: "bank_account_name", label: "Atas Nama", type: "string" },
        ]}
        sampleRow={{ name: "PT Supplier Contoh", contact: "Ahmad", phone: "021-9876543", email: "info@sup.co.id", address: "Bandung", npwp: "01.234.567.8-901.000", nib: "1234567890123", sppkp: "PEM-123/WPJ.04/2024", bank_name: "BCA", bank_account: "1234567890", bank_account_name: "PT Supplier Contoh" }}
      />
    </div>
  );
}
